import { useEffect, useRef, useState } from 'react';
import { useT, type Clave } from '../i18n/index.js';
import { abrirCamara, clasificarFalla, type FallaCamara } from '../lib/escaner.js';
import { useHayCamara } from './Escaner.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

type Fase = 'abriendo' | 'viendo' | FallaCamara;

/** The scanner's words for what went wrong, but for a photo that can also come from «Galería». */
const FALLA: Record<FallaCamara, [Clave, Clave]> = {
  permiso: ['escaner.falla.permiso', 'escaner.falla.permisoAyuda'],
  sinCamara: ['escaner.falla.sinCamara', 'foto.camara.sinCamaraAyuda'],
  ocupada: ['escaner.falla.ocupada', 'escaner.falla.ocupadaAyuda'],
  lector: ['escaner.falla.otra', 'escaner.falla.otraAyuda'],
  otra: ['escaner.falla.otra', 'escaner.falla.otraAyuda'],
};

/**
 * Whether «Tomar foto» opens the camera here, in the page: on a computer that has one. A phone or a
 * tablet, a finger for a pointer, opens its own camera app through the file input's `capture`,
 * which focuses, lights and steadies a photo better than a page can; a computer with no camera
 * offers a file.
 */
export function useCamaraEnLaPagina(): boolean {
  const hay = useHayCamara();
  return hay && !(window.matchMedia?.('(pointer: coarse)').matches ?? false);
}

/**
 * La cámara de la computadora, para la foto de un producto (the owner, 2026-10-20: «can we also have
 * the same type of directly from camera to upload feature on webcam»).
 *
 * A phone's «Tomar foto» opens its camera app, which takes the photo and hands it straight back. A
 * computer's browser ignores `capture` and offers a file, a photo taken somewhere else first. So on a
 * computer with a camera the page is the camera: the picture live in a square, which is the square
 * the photo keeps (`recorteCuadrado`), «Tomar foto», then the photo held still with «Usar foto» and
 * «Otra vez», as a phone's camera app asks. What is used goes to `onTomada` as a picture, and through
 * what any file picked goes through.
 *
 * The live picture is a mirror, as a webcam's always is, so a product moved left goes left on the
 * screen; the photo is not, so its label reads the right way round, as the still shows. The camera
 * stops the moment this closes, and closes when the page is hidden, as the scanner's does.
 */
export function CamaraFoto({
  titulo,
  onTomada,
  onCerrar,
}: {
  titulo: string;
  onTomada: (foto: Blob) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const video = useRef<HTMLVideoElement>(null);
  const disparador = useRef<HTMLButtonElement>(null);
  const [fase, setFase] = useState<Fase>('abriendo');
  const [intento, setIntento] = useState(0);
  /** A camera facing whoever is at the screen, as a webcam does: drawn as a mirror. */
  const [espejo, setEspejo] = useState(true);
  /** The photo taken, held still. */
  const [tomada, setTomada] = useState<{ foto: Blob; url: string } | null>(null);
  const cerrar = useRef(onCerrar);
  cerrar.current = onCerrar;

  useEffect(() => {
    let vivo = true;
    let stream: MediaStream | null = null;
    const v = video.current;
    setFase('abriendo');
    void (async () => {
      try {
        stream = await abrirCamara();
      } catch (e) {
        if (vivo) setFase(clasificarFalla(e));
        return;
      }
      if (!vivo) {
        stream.getTracks().forEach((pista) => pista.stop());
        return;
      }
      setEspejo(stream.getVideoTracks()[0]?.getSettings().facingMode !== 'environment');
      if (v) {
        v.muted = true;
        v.srcObject = stream;
        await v.play().catch(() => undefined);
      }
      if (vivo) setFase('viendo');
    })();
    return () => {
      vivo = false;
      stream?.getTracks().forEach((pista) => pista.stop());
      if (v) v.srcObject = null;
    };
  }, [intento]);

  useEffect(() => {
    const alOcultar = () => {
      if (document.hidden) cerrar.current();
    };
    document.addEventListener('visibilitychange', alOcultar);
    return () => document.removeEventListener('visibilitychange', alOcultar);
  }, []);

  // The shutter takes the keys once the picture is live, and again after «Otra vez»: Enter, Enter.
  useEffect(() => {
    if (fase === 'viendo' && !tomada) disparador.current?.focus();
  }, [fase, tomada]);

  useEffect(() => {
    if (!tomada) return;
    return () => URL.revokeObjectURL(tomada.url);
  }, [tomada]);

  /** The frame on screen, as the camera sees it: whole, and the right way round. */
  async function disparar() {
    const v = video.current;
    if (!v || fase !== 'viendo' || !v.videoWidth) return;
    const lienzo = document.createElement('canvas');
    lienzo.width = v.videoWidth;
    lienzo.height = v.videoHeight;
    lienzo.getContext('2d')?.drawImage(v, 0, 0);
    const foto = await new Promise<Blob | null>((listo) => lienzo.toBlob(listo, 'image/jpeg', 0.92));
    if (foto) setTomada({ foto, url: URL.createObjectURL(foto) });
  }

  const falla = fase === 'abriendo' || fase === 'viendo' ? null : fase;

  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        tomada ? (
          // Side by side down to 320 px, each word on one line: «Use photo» broke in two there.
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secundario flex-1 whitespace-nowrap max-[379px]:px-3"
              onClick={() => setTomada(null)}
              data-camara-otra
            >
              <Ico icono="reiniciar" />
              {t('foto.camara.otraVez')}
            </button>
            <button
              type="button"
              className="btn-primario flex-1 whitespace-nowrap max-[379px]:px-3"
              onClick={() => onTomada(tomada.foto)}
              autoFocus
              data-camara-usar
            >
              <Ico icono="listo" />
              {t('foto.camara.usar')}
            </button>
          </div>
        ) : (
          <button
            ref={disparador}
            type="button"
            className="btn-primario w-full"
            disabled={fase !== 'viendo'}
            onClick={() => void disparar()}
            data-camara-disparar
          >
            <Ico icono="foto" />
            {t('foto.tomar')}
          </button>
        )
      }
    >
      {/* No taller than most of the screen, so «Tomar foto» under it is in sight on a laptop too. */}
      <div className="relative mx-auto aspect-square w-full max-w-[min(100%,55dvh)] overflow-hidden rounded-2xl bg-black" data-camara>
        <video
          ref={video}
          className={`size-full object-cover ${espejo ? '-scale-x-100' : ''} ${tomada ? 'invisible' : ''}`}
          muted
          playsInline
          autoPlay
        />
        {tomada && <img src={tomada.url} alt="" className="absolute inset-0 size-full object-cover" data-camara-tomada />}
        {fase === 'abriendo' && (
          <p className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-slate-300">
            {t('escaner.abriendo')}
          </p>
        )}
        {falla && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <Ico icono="aviso" className="size-8 text-amber-300" />
            <p className="font-semibold">{t(FALLA[falla][0])}</p>
            <p className="text-sm text-slate-400">{t(FALLA[falla][1])}</p>
            <button type="button" className="btn-secundario" onClick={() => setIntento((n) => n + 1)}>
              <Ico icono="reiniciar" />
              {t('escaner.reintentar')}
            </button>
          </div>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-slate-400">{t(tomada ? 'foto.camara.asiQueda' : 'foto.camara.ayuda')}</p>
    </Modal>
  );
}
